import { Injectable, inject } from "@angular/core";
import { FgdsProductType } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { AttachmentDataService } from "@domain/project/product-data/attachment-data.service";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { Observable, catchError, defaultIfEmpty, forkJoin, map, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class TransmitterDataService {
  private productService = inject(ProductService);
  private attachmentDataService = inject(AttachmentDataService);

  getTransmitterData(
    transmitters: readonly TransmitterConfiguration[],
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ProductData[][]> {
    if (transmitters.length == 0) {
      return of([]);
    }
    return forkJoin(transmitters.map((transmitter) => this.getProducts(transmitter, incompleteConfigIds, localeId)));
  }

  assignProductToConfiguration(
    configs: TransmitterConfiguration[],
    uniqueConfigs: TransmitterConfiguration[],
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ConfigProductMap[]> {
    if (uniqueConfigs.length === 0) {
      return of([]);
    }
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfig) =>
        this.getProducts(uniqueConfig, incompleteConfigIds, localeId).pipe(
          map((productData) => {
            const placedProductsCount = configs.filter((config) => config.id === uniqueConfig.id).length;
            return new ConfigProductMap(uniqueConfig, productData, placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts(
    transmitterConfig: TransmitterConfiguration,
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ProductData[]> {
    const attachmentStartingIndex = transmitterConfig.sensorId ? 1 : 0;
    const transmitter$ = this.getTransmitterProductData(transmitterConfig, incompleteConfigIds, localeId);

    const sensor$: Observable<ProductData | undefined> = this.getSensorData(transmitterConfig, localeId);
    const attachments$: Observable<ProductData[]> = this.attachmentDataService.getProductData(
      transmitterConfig,
      transmitterConfig.attachments,
      localeId,
      attachmentStartingIndex,
    );

    return forkJoin([transmitter$, sensor$, attachments$]).pipe(
      map((results: any[]) => {
        const transmitterData = results[0];
        const attachmentData = results[2];
        const sensorData = results[1];

        const productData = [transmitterData];
        if (sensorData) {
          productData.push(sensorData);
        }
        productData.push(...attachmentData);
        return productData.filter((product): product is ProductData => !!product);
      }),
    );
  }

  private getTransmitterProductData(
    transmitterConfig: TransmitterConfiguration,
    incompleteConfigIds: string[],
    localeId: string,
  ) {
    return this.productService.getProductById(transmitterConfig.productId).pipe(
      map((product) => {
        if (!product || product.type !== FgdsProductType.TRANSMITTER) {
          return undefined;
        }
        const productData = ProductData.create(
          product,
          localeId,
          this.productService.getFilterCountry()!,
          1,
          ProductListDiscountSlot.MAIN,
        );
        productData.positionId = transmitterConfig.positionNumber!;
        productData.isIncompleteConfiguration = incompleteConfigIds.includes(transmitterConfig.id);
        return productData;
      }),
      defaultIfEmpty(undefined),
      catchError(() => of(undefined)),
    );
  }

  private getSensorData(transmitterConfig: TransmitterConfiguration, localeId: string) {
    if (!transmitterConfig.sensorId) {
      return of(undefined);
    }

    return this.productService.getProductById(transmitterConfig.sensorId).pipe(
      map((sensor) => {
        if (!sensor || sensor.type !== FgdsProductType.SENSOR) {
          return undefined;
        }
        const sensorData = ProductData.create(
          sensor,
          localeId,
          this.productService.getFilterCountry()!,
          1,
          ProductListDiscountSlot.SENSOR,
        );
        sensorData.positionId = transmitterConfig.positionNumber! + ".a";
        return sensorData;
      }),
      defaultIfEmpty(undefined),
      catchError(() => of(undefined)),
    );
  }
}
